import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Maximize2, Printer, Share2, Download, X } from 'lucide-react';

// Reusable brand-flyer viewer: click-to-enlarge, Print (8x10 PDF-ready), Share, Download.
const FlyerViewer = ({ src, alt = 'INASW flyer', className = '', rounded = true, caption }) => {
  const [open, setOpen] = useState(false);
  const absUrl = src?.startsWith('http') ? src : `${window.location.origin}${src}`;

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=850,height=1100');
    if (!w) { toast.error('Please allow pop-ups to print.'); return; }
    const doc = w.document;
    // Build the print document with safe DOM APIs (no document.write / no HTML string interpolation)
    doc.title = alt;
    const style = doc.createElement('style');
    style.textContent = '@page{size:8in 10in;margin:0.25in;}'
      + 'html,body{margin:0;padding:0;height:100%;}'
      + '.wrap{display:flex;align-items:center;justify-content:center;height:100%;}'
      + 'img{max-width:100%;max-height:100%;object-fit:contain;}';
    doc.head.appendChild(style);
    const wrap = doc.createElement('div');
    wrap.className = 'wrap';
    const img = doc.createElement('img');
    img.alt = alt;
    img.addEventListener('load', () => setTimeout(() => { w.focus(); w.print(); }, 250));
    img.src = absUrl; // treated as an attribute value, never parsed as HTML
    wrap.appendChild(img);
    doc.body.appendChild(wrap);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: alt, text: 'From INeedASocialWorker.com', url: absUrl });
      } else {
        await navigator.clipboard.writeText(absUrl);
        toast.success('Image link copied to clipboard.');
      }
    } catch (e) { /* user cancelled */ }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className={`group relative block w-full overflow-hidden ${rounded ? 'rounded-2xl' : ''} ${className}`}
        aria-label="Enlarge flyer" data-testid="flyer-thumb">
        <img src={src} alt={alt} className="w-full h-auto" />
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-primary text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1">
            <Maximize2 className="w-3.5 h-3.5" /> Click to enlarge
          </span>
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl p-0 overflow-hidden" data-testid="flyer-dialog">
          <DialogTitle className="sr-only">{alt}</DialogTitle>
          <div className="flex items-center justify-end gap-2 p-3 border-b bg-white sticky top-0 z-10">
            <Button size="sm" variant="outline" onClick={handlePrint} data-testid="flyer-print"><Printer className="w-4 h-4 mr-1" /> Print (8×10)</Button>
            <Button size="sm" variant="outline" onClick={handleShare} data-testid="flyer-share"><Share2 className="w-4 h-4 mr-1" /> Share</Button>
            <a href={src} download><Button size="sm" variant="outline" data-testid="flyer-download"><Download className="w-4 h-4 mr-1" /> Download</Button></a>
            <button onClick={() => setOpen(false)} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid="flyer-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="max-h-[75vh] overflow-auto p-4 bg-gray-50">
            <img src={src} alt={alt} className="w-full h-auto rounded-lg" />
          </div>
        </DialogContent>
      </Dialog>
      {caption && <p className="text-center text-xs text-gray-400 mt-2">{caption}</p>}
    </>
  );
};

export default FlyerViewer;
